import React from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import { useAppDispatch, useAppSelector } from '@/features/hooks'
import { sendMfaOtpToHotel } from '@/features/mfa/mfa-thunks'

type OtpMailHelpPopupProps = {
  idToken: string
  hotelEmail?: string
  onSent: () => void
  onCancel: () => void
}

export const OtpMailHelpPopup: React.FC<OtpMailHelpPopupProps> = ({ idToken, hotelEmail, onSent, onCancel }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const hotelSend = useAppSelector(state => state.mfa.hotelSend)
  const isBusy = hotelSend.status === 'sending'

  const onSendToHotel = async () => {
    const result = await dispatch(sendMfaOtpToHotel(idToken))
    if (sendMfaOtpToHotel.fulfilled.match(result)) {
      onSent()
    }
  }

  return (
    // 送信中に閉じると、失敗したときのエラーを出す場所がなくなり、送れたと誤解するため閉じさせない
    <div css={overlayStyle} onClick={isBusy ? undefined : onCancel}>
      <div css={popupStyle} onClick={event => event.stopPropagation()}>
        <p css={titleStyle}>メールを受け取れない場合</p>
        <p css={descriptionStyle}>メールが受信できない場合、施設の代表メールアドレスに確認コードを再送します。</p>

        <div css={emailBoxStyle}>
          <p css={emailLabelStyle}>施設代表メールアドレス</p>
          <p css={emailValueStyle}>{hotelEmail || '-'}</p>
        </div>

        {hotelSend.error ? <p css={errorTextStyle}>{t(hotelSend.error)}</p> : null}

        <Button
          buttonType={isBusy ? ButtonType.PrimaryDisabled : ButtonType.Primary}
          disabled={isBusy}
          width={275}
          height={38}
          marginTop={22}
          marginLeft={'auto'}
          marginRight={'auto'}
          onClick={onSendToHotel}
        >
          施設代表メールに送る
        </Button>

        <Button
          buttonType={isBusy ? ButtonType.BorderDisabled : ButtonType.Secondary}
          disabled={isBusy}
          width={275}
          height={38}
          marginTop={14}
          marginLeft={'auto'}
          marginRight={'auto'}
          onClick={onCancel}
        >
          キャンセル
        </Button>
      </div>
    </div>
  )
}

const overlayStyle = css({
  position: 'fixed',
  inset: 0,
  zIndex: 1200,
  background: 'rgba(0, 0, 0, 0.52)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '16px',
})

const popupStyle = css({
  width: 400,
  maxWidth: '92vw',
  background: '#FFFFFF',
  borderRadius: 5,
  padding: '40px 28px 34px',
})

const baseTextStyle = css({
  fontSize: 12,
  color: '#272727',
  lineHeight: 1.5,
})

const titleStyle = css(baseTextStyle, {
  fontSize: 18,
  fontWeight: 700,
  margin: 0,
})

const descriptionStyle = css(baseTextStyle, {
  margin: '18px 0 0',
  color: '#676767',
})

const emailBoxStyle = css({
  marginTop: 16,
  borderRadius: 5,
  background: '#F4F7FC',
  padding: '14px 18px',
})

const emailLabelStyle = css(baseTextStyle, {
  margin: 0,
})

const emailValueStyle = css(baseTextStyle, {
  margin: '4px 0 0',
  fontWeight: 700,
})

const errorTextStyle = css(baseTextStyle, {
  margin: '14px 0 0',
  color: '#D34A35',
})
